import { ReactElement, useEffect, useState } from "react";
import {
	Dialog,
	DateString,
	Toolbar,
	Space
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import { navigateRelativeToEnvironment } from "client_legacy/ndaw/navigator";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import {
	getUserRequest,
	UserRequestDetailsClientModel,
	UserRequestRespondedClientModel,
	UserRequestRespondKind
} from "client/Api/UserRequests/manageUserRequests";
import { UserRequestStatus } from "client/Api/UserRequests/userRequest";
import { getUserRequestItemName, getUserRequestKindText, getUserRequestStatusText } from "./common";
import { RespondToUserRequestDialog } from "./RespondToUserRequestDialog";
import { setTrackingId } from "client/Misc/utils";
import {
	AlertManager,
	useAlerts,
	useUserTimezone
} from "client/Hooks";
import { TimeZone } from "client/DateAndTime/timezones";

export type UserRequestDetailsDialogProps = {
	id: number;
	onClose(): void;
	onResponded(response: readonly UserRequestRespondedClientModel[]): void;
	isOpenedFromOverview?: boolean;
};

export const UserRequestDetailsDialog = (props: UserRequestDetailsDialogProps): ReactElement => {
	const {
		id,
		onClose,
		onResponded,
		isOpenedFromOverview = false
	} = props;

	const [isInteractingWithTheBackend, setIsInteractingWithTheBackend] = useState<boolean>(true);
	const [details, setDetails] = useState<UserRequestDetailsClientModel>();
	const [pendingResponse, setPendingResponse] = useState<UserRequestRespondKind | undefined>();
	const alertManager: AlertManager = useAlerts();
	const userTimezone: TimeZone = useUserTimezone();

	useEffect(
		() => {
			const load = async (id: number, signal: AbortSignal): Promise<void> => {
				try {
					setIsInteractingWithTheBackend(true);
					const userRequest: UserRequestDetailsClientModel = await getUserRequest(id, signal);
					setDetails(userRequest);
					setIsInteractingWithTheBackend(false);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}
					alertManager.showError(localize("_GeneralErrorMessage"));
					onClose();
				}
			};

			const cancellation = new AbortController();
			load(id, cancellation.signal);
			return () => cancellation.abort();
		},
		[alertManager, id, onClose]
	);

	const acceptHandler = (): void => {
		setPendingResponse(UserRequestRespondKind.Accept);
	};

	const declineHandler = (): void => {
		setPendingResponse(UserRequestRespondKind.Decline);
	};

	const openOverviewHandler = (): void => {
		navigateRelativeToEnvironment("UserRequests/Manage");
	};

	if (typeof pendingResponse !== "undefined") {
		return (
			<RespondToUserRequestDialog
				ids={[id]}
				kind={pendingResponse}
				onClose={() => setPendingResponse(undefined)}
				onResponded={onResponded}
			/>
		);
	}

	return (
		<Dialog
			trackingId="user-request-details-dialog"
			hasBusyIndicator={isInteractingWithTheBackend}
		>
			<Dialog.Header
				title={localize("_RequestDetails")}
				onClose={onClose}
			/>
			<Dialog.Body>
				{details && (
					<p>
						<strong>{localize("_RequestedItem")}:</strong> <span {...setTrackingId("item-name")}>{getUserRequestItemName(details.item)}</span> <br/>
						<strong>{localize("_Type")}:</strong> <span {...setTrackingId("type")}>{getUserRequestKindText(details.item.kind)}</span><br/>
						<strong>{localize("_RequestedBy")}:</strong> <span {...setTrackingId("requested-by")}>{details.requester.name}</span> <br/>
						<strong>{localize("_Email")}:</strong> <span {...setTrackingId("email")}>{details.requester.email}</span> <br/>
						<strong>{localize("_RequestDate")}:</strong>
						<Space />
						<span {...setTrackingId("request-date")}>
							<DateString
								value={details.createdOn}
								format="DMYT"
								timezone={userTimezone}
							/>
						</span>
						<Space />
						<br/>
						<strong>{localize("_ReasonForRequest")}:</strong> <span {...setTrackingId("reason")}>{details.reasonForRequest}</span> <br/>
						<strong>{localize("_Status")}:</strong> <span {...setTrackingId("status")}>{getUserRequestStatusText(details.status)}</span> <br/>
						{ details.status !== UserRequestStatus.Open && (
							<>
								<strong>{localize("_RespondDate")}:</strong>
								<Space />
								<span {...setTrackingId("respond-date")}>
									<DateString
										value={details.respondedOn}
										format="DMYT"
										timezone={userTimezone}
									/>
								</span>
								<Space />
								<br/>
								<strong>{localize("_RespondedBy")}:</strong> <span {...setTrackingId("responded-by")}>{details.responder?.name}</span> <br/>
								{ details.responseNote && (
									<>
										<strong>{localize("_ResponseNote")}:</strong> <span {...setTrackingId("response-note")}>{details.responseNote}</span> <br/>
									</>
								)}
							</>
						)}
					</p>
				)}

			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					{!isOpenedFromOverview && (
						<Toolbar.Button
							kind="transparent"
							icon="folder-open"
							text={localize("_OpenRequestsOverview")}
							onClick={openOverviewHandler}
							trackingId="open-overview"
						/>
					)}
					<Toolbar.SpaceBetween />
					{(details && details.status === UserRequestStatus.Open) && (
						<>
							<Toolbar.Button kind="primary" text={localize("_Accept")} onClick={acceptHandler} trackingId="accept" />
							<Toolbar.Button kind="primary" text={localize("_Decline")} onClick={declineHandler} trackingId="decline" />
						</>
					)}
					<Toolbar.Button
						kind="secondary"
						text={localize("_Close")}
						onClick={onClose}
						trackingId="close"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</Dialog>
	);
};
UserRequestDetailsDialog.displayName = "UserRequestDetailsDialog";